/**
 * TemplateSelector
 * Component for selecting book templates
 */

import { useState } from 'react'
import { BookTemplate, BOOK_TEMPLATES } from '@/core/models'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Badge } from '../ui'
import { Check } from 'lucide-react'
import { clsx } from 'clsx'

export interface TemplateSelectorProps {
  onSelect: (template: BookTemplate) => void
  selectedTemplateId?: string
}

export function TemplateSelector({ onSelect, selectedTemplateId }: TemplateSelectorProps) {
  const [selected, setSelected] = useState<string>(selectedTemplateId || 'blank')

  const handleSelect = (template: BookTemplate) => {
    setSelected(template.id)
    onSelect(template)
  }

  const getCategoryColor = (category: BookTemplate['category']) => {
    switch (category) {
      case 'fiction':
        return 'bg-purple-100 text-purple-700'
      case 'non-fiction':
        return 'bg-blue-100 text-blue-700'
      case 'educational':
        return 'bg-green-100 text-green-700'
      case 'business':
        return 'bg-orange-100 text-orange-700'
      case 'creative':
        return 'bg-pink-100 text-pink-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose a Template</h3>
        <p className="text-sm text-gray-600">
          Start with a pre-made structure or begin with a blank book
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
        {BOOK_TEMPLATES.map(template => (
          <Card
            key={template.id}
            padding="sm"
            hover
            onClick={() => handleSelect(template)}
            className={clsx(
              'cursor-pointer transition-all',
              selected === template.id
                ? 'ring-2 ring-blue-500 bg-blue-50'
                : 'hover:border-blue-300',
            )}
          >
            <CardHeader className="mb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{template.icon}</span>
                  <div>
                    <CardTitle className="text-sm sm:text-base">{template.name}</CardTitle>
                    <div className="flex gap-1 mt-1">
                      <Badge
                        variant="default"
                        className={clsx('text-xs', getCategoryColor(template.category))}
                      >
                        {template.category}
                      </Badge>
                    </div>
                  </div>
                </div>
                {selected === template.id && (
                  <div className="bg-blue-500 rounded-full p-1">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <CardDescription className="text-xs sm:text-sm line-clamp-2">
                {template.description}
              </CardDescription>
              {template.chapters.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {template.chapters.length} chapter{template.chapters.length !== 1 ? 's' : ''}{' '}
                  included
                </p>
              )}
              {template.metadata && (
                <div className="mt-2 space-y-1">
                  {template.metadata.recommendedLength && (
                    <p className="text-xs text-gray-500">
                      📏 {template.metadata.recommendedLength}
                    </p>
                  )}
                  {template.metadata.targetAudience && (
                    <p className="text-xs text-gray-500">
                      👥 {template.metadata.targetAudience}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {selected && selected !== 'blank' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-900 mb-2">
            {BOOK_TEMPLATES.find(t => t.id === selected)?.name} Template
          </p>
          <p className="text-xs text-blue-700">
            This template includes{' '}
            {BOOK_TEMPLATES.find(t => t.id === selected)?.chapters.length} pre-written chapter
            {BOOK_TEMPLATES.find(t => t.id === selected)?.chapters.length !== 1 ? 's' : ''} with
            guiding content to help you get started.
          </p>
          {BOOK_TEMPLATES.find(t => t.id === selected)?.metadata?.tips && (
            <div className="mt-3">
              <p className="text-xs font-medium text-blue-900 mb-1">💡 Tips:</p>
              <ul className="text-xs text-blue-700 space-y-1">
                {BOOK_TEMPLATES.find(t => t.id === selected)
                  ?.metadata?.tips?.slice(0, 2)
                  .map((tip, index) => (
                    <li key={index}>• {tip}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
